<template>
    <form
        autocomplete="off"
        :data-test="dataTest"
        @submit="onSubmit"
    >
        <NoteMentionInput
            v-model:mentioned-user-ids="mentionedUserIds"
            vid="comment"
            :placeholder="placeholder"
            :rows="rows"
        />
        <div class="mb-6 flex justify-end gap-x-2">
            <MuvalButton
                v-if="cancellable"
                quaternary
                type="button"
                :disabled="isSubmitting"
                data-test="composer-cancel"
                @click="$emit('cancel')"
            >
                Cancel
            </MuvalButton>
            <MuvalButton
                :icon="submitIcon"
                tertiary
                :loading="isSubmitting"
                :disabled="isSubmitting"
                data-test="composer-submit"
            >
                {{ submitLabel }}
            </MuvalButton>
        </div>
    </form>
</template>

<script setup>
import { useForm } from 'vee-validate';
import { useModalUnsavedChanges } from '@composables/useModalUnsavedChanges';
import MuvalButton from '@buttons/MuvalButton.vue';
import NoteMentionInput from '@components/order/NoteMentionInput.vue';

/**
 * One note composer — a comment and its @mentions, and nothing else.
 *
 * Its own component rather than a block inside `SalesActionNotes.vue` because the
 * thread shows two of them at once (the root composer, and a reply composer under
 * whichever note is being replied to) and `useForm` establishes a single form
 * context per component: two calls in one setup would leave both fields sharing —
 * and clobbering — one set of values and errors.
 *
 * An action has no order lines to attach a note to and no issue flag of its own to
 * raise, so neither is offered here.
 */
const props = defineProps({
    /**
     * Performs the write. Awaited, so the button holds its loading state for the
     * real duration of the request and a rejection leaves the typed text in place
     * to be retried rather than discarding it.
     */
    submit: {
        type: Function,
        required: true,
    },
    placeholder: {
        type: String,
        default: 'Add a note',
    },
    submitLabel: {
        type: String,
        default: 'Add note',
    },
    rows: {
        type: Number,
        default: 3,
    },
    /** Reply composers can be dismissed; the thread's root composer cannot. */
    cancellable: {
        type: Boolean,
        default: false,
    },
    dataTest: {
        type: String,
        default: 'note-form',
    },
    /**
     * Seeds an edit. Left empty for a new note, which is why the form's initial
     * values are read from here rather than hard-coded: the same component serves
     * the root composer, a reply composer and an edit of an existing note, and only
     * the last of the three starts with text in it.
     */
    initialComment: {
        type: String,
        default: '',
    },
    initialMentionedUserIds: {
        type: Array,
        default: () => [],
    },
    /** `system-add` reads as "another one" — wrong on an edit, which replaces. */
    submitIcon: {
        type: [String, null],
        default: 'system-add',
    },
});

defineEmits(['cancel']);

const mentionedUserIds = ref([...props.initialMentionedUserIds]);

const { handleSubmit, resetForm, setErrors, isSubmitting, values } = useForm({
    initialValues: { comment: props.initialComment },
});

/**
 * Declared here rather than in the thread around it (CU-86d3wvwq7): three of these can be on screen
 * at once, and the modal asks each for itself. An edit composer seeds with the note's saved text, so
 * the baseline is taken after mount and an untouched edit reads clean.
 */
const unsavedChanges = useModalUnsavedChanges(() => ({
    comment: values.comment,
    mentioned_user_ids: mentionedUserIds.value,
}));

const onSubmit = handleSubmit(async (values) => {
    await props.submit({
        comment: values.comment,
        mentioned_user_ids: mentionedUserIds.value,
    });
});

/** Called by the parent once the write has landed, or when the thread is discarded. */
function reset() {
    mentionedUserIds.value = [...props.initialMentionedUserIds];
    resetForm({ values: { comment: props.initialComment } });
    // Re-baseline, or a sent note would leave the modal reporting unsaved changes for ever.
    nextTick(() => unsavedChanges.markClean());
}

defineExpose({ reset, setErrors });
</script>
